import React, { useState, useEffect } from "react";
import {
  fetchPromoterPayouts,
  payPromoterPayout,
} from "../../services/promoterPayoutService";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import { showConfirmationDialog } from "../../utils/apiHelper";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatPayoutDate } from "./PayoutTransactionsTable";

const PromoterPayoutListLayer = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [payingId, setPayingId] = useState(null);

  const formatDate = (dateStr) => formatPayoutDate(dateStr);

  const formatAmount = (value, currency = "cad") => {
    const num = Number(value);
    const amount = Number.isFinite(num) ? num.toFixed(2) : "0.00";
    return `${String(currency || "cad").toUpperCase()} ${amount}`;
  };

  const getDisplayStatus = (item) => {
    const raw = String(
      item?.status || item?.transactionStatus || "",
    ).toLowerCase();
    if (raw === "paid" || raw === "completed" || raw === "success") {
      return "paid";
    }
    return "pending";
  };

  const getStatusClass = (status) => {
    if (status === "paid") {
      return "bg-success-focus text-success-600 border border-success-main";
    }
    return "bg-warning-focus text-warning-600 border border-warning-main";
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const formattedStartDate = startDate
        ? moment(startDate).format("YYYY-MM-DD")
        : "";
      const formattedEndDate = endDate
        ? moment(endDate).format("YYYY-MM-DD")
        : "";

      if (
        formattedStartDate &&
        formattedEndDate &&
        moment(formattedStartDate).isAfter(moment(formattedEndDate))
      ) {
        setErrorMessage("Start date cannot be greater than end date");
        setTimeout(() => setErrorMessage(""), 3000);
        return;
      }

      const res = await fetchPromoterPayouts({
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery,
        sortBy: sortColumn,
        sortOrder,
        status: statusFilter,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });

      const payload = res?.data || {};
      setItems(payload.data || payload.payouts || []);
      setTotalItems(payload.pagination?.total || 0);
      setTotalPages(payload.pagination?.totalPages || 1);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Failed to fetch promoter payouts",
      );
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    searchQuery,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortOrder,
    statusFilter,
    startDate,
    endDate,
  ]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const handlePayNow = async (item) => {
    if (!item?._id) return;

    const result = await showConfirmationDialog(
      "This action will pay the promoter payout now.",
      "Yes, pay now!",
    );
    if (!result.isConfirmed) return;

    try {
      setPayingId(item._id);
      const response = await payPromoterPayout(item._id);
      setSuccessMessage(
        response?.data?.message || "Payout paid successfully",
      );
      fetchData();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message || err?.message || "Failed to pay payout",
      );
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setPayingId(null);
    }
  };

  const getPaginationRange = () => {
    const maxVisiblePages = 5;
    const pages = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      for (let i = startPage; i <= endPage; i++) pages.push(i);
    }

    return pages;
  };

  const renderSortIcons = (column) => (
    <span className="sort-colume">
      <Icon
        icon="mdi:arrow-up"
        className={`arrow ${
          sortColumn === column && sortOrder === "asc" ? "active" : ""
        }`}
      />
      <Icon
        icon="mdi:arrow-down"
        className={`arrow ${
          sortColumn === column && sortOrder === "desc" ? "active" : ""
        }`}
      />
    </span>
  );

  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value, 10));
              setCurrentPage(1);
            }}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
            <option value="100">100 items per page</option>
          </select>

          <form className="navbar-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              name="search"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                paddingRight: searchQuery ? "5rem" : "2.5rem",
              }}
            />
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: searchQuery ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>

          <div style={{ width: "160px" }}>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                setStartDate(date);
                setCurrentPage(1);
              }}
              onKeyDown={(e) => e.preventDefault()}
              className="form-control no-caret"
              placeholderText="Start Date"
              dateFormat="yyyy-MM-dd"
              isClearable
            />
          </div>
          <div style={{ width: "160px" }}>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                setEndDate(date);
                setCurrentPage(1);
              }}
              onKeyDown={(e) => e.preventDefault()}
              className="form-control no-caret"
              placeholderText="End Date"
              dateFormat="yyyy-MM-dd"
              isClearable
            />
          </div>

          <select
            className="form-select form-select-sm w-auto ps-12"
            style={{
              height: "40px",
              minHeight: "40px",
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
          </select>
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("promoterName")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Promoter Name
                    {renderSortIcons("promoterName")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("fromDate")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Start Date
                    {renderSortIcons("fromDate")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("toDate")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    End Date
                    {renderSortIcons("toDate")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("status")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Transaction Status
                    {renderSortIcons("status")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("amount")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Amount
                    {renderSortIcons("amount")}
                  </div>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((item) => {
                  const displayStatus = getDisplayStatus(item);
                  return (
                    <tr key={item._id}>
                      <td>{item.promoterName || "N/A"}</td>
                      <td>{formatDate(item.fromDate)}</td>
                      <td>{formatDate(item.toDate)}</td>
                      <td>
                        <span
                          className={`${getStatusClass(
                            displayStatus,
                          )} px-24 py-4 radius-4 fw-medium text-sm`}
                        >
                          {displayStatus.charAt(0).toUpperCase() +
                            displayStatus.slice(1)}
                        </span>
                      </td>
                      <td>{formatAmount(item.amount, item.currency)}</td>
                      <td>
                        <div className="d-flex align-items-center gap-10">
                          {displayStatus === "pending" && (
                            <button
                              type="button"
                              title="Pay Now"
                              disabled={payingId === item._id}
                              onClick={() => handlePayNow(item)}
                              className="bg-success-focus bg-hover-success-200 text-success-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            >
                              <Icon
                                icon="mdi:cash-fast"
                                className="icon text-xl"
                              />
                            </button>
                          )}
                          <button
                            type="button"
                            title="View"
                            onClick={() =>
                              navigate(
                                `/promoter-payout/view-payout/${item._id}`,
                                { state: { payout: item } },
                              )
                            }
                            className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon
                              icon="majesticons:eye-line"
                              className="icon text-xl"
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            {totalItems === 0 ? (
              <>No entry to show</>
            ) : (
              <>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} entries
              </>
            )}
          </span>

          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                onClick={() =>
                  currentPage > 1 && setCurrentPage((prev) => prev - 1)
                }
              >
                <Icon icon="ep:d-arrow-left" />
              </button>
            </li>
            {getPaginationRange().map((pageNum) => (
              <li key={pageNum} className="page-item">
                <button
                  className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                    currentPage === pageNum
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200"
                  }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                disabled={currentPage === totalPages}
                onClick={() =>
                  currentPage < totalPages &&
                  setCurrentPage((prev) => prev + 1)
                }
              >
                <Icon icon="ep:d-arrow-right" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PromoterPayoutListLayer;
